import * as Sentry from "@sentry/nextjs"
import { useCallback } from "react"

import useToast from "@hooks/useToast"
import { ParticipationDetailsFormAttributes } from "@modules/tenants/OrdersPage/Components/AddOrderModal/ParticipationDetailsForm/ParticipationDetailsForm"
import { ActivityDetailsFormAttributes } from "@modules/tenants/OrdersPage/ViewOrderPage/Components/ActivityRescheduleModal/ActivityDetailsForm"
import { addonDetailsUntType } from "@mono/essentials/entities/cart/types"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import {
  AddAddonToOrderItemRequestType,
  GetOrderDetailsResponseType,
  GetParticipantsOfOrderItemResponseType,
  UpdatePerParticipantFieldsOfOrderRequestType,
} from "@mono/essentials/types/OrderTypes"

import { useHandleRescheduleActivityBooking } from "./handleRescheduleActivityBooking"
import { useHandleUpdateBookingDetailsOfOrderItem } from "./handleUpdateBookingDetailsOfOrderItem"

export const useHandleRescheduleActivityBookingWithFields = () => {
  const { showToast } = useToast()
  const { handleRescheduleActivityBooking } = useHandleRescheduleActivityBooking()
  const { handleUpdateBookingDetailsOfOrderItem } = useHandleUpdateBookingDetailsOfOrderItem()
  const handleRescheduleActivityBookingWithFields = useCallback(
    async ({
      productInfo,
      participantsInfo,
      orderItemId,
      onSuccess,
      onError,
      orderData,
    }: {
      productInfo: ActivityDetailsFormAttributes
      participantsInfo: ParticipationDetailsFormAttributes
      orderItemId: string
      orderData: GetOrderDetailsResponseType
      onSuccess?: () => Promise<void>
      onError?: (error: any) => Promise<void>
    }) => {
      try {
        if (productInfo && orderData?.id && orderItemId) {
          const selectedOrderItem = orderData.items.find((oi) => oi.id === orderItemId)

          // Reschedule
          await handleRescheduleActivityBooking({
            orderItemId,
            productInfo,
            orderData,
          })

          // Participants before quantity update
          const {
            data: { participants: participantsInfoBeforeQuantityUpdate },
          } = await OrderServices.getParticipantsOfOrderItem(orderData.id, orderItemId)

          // Update quantity
          const newQuantities = productInfo?.units?.filter((unitInfo) => unitInfo.unitCount > 0)
          await OrderServices.updateQuantityOfOrderItem(orderData.id, orderItemId, {
            quantities: newQuantities.map((unit) => ({
              unit_id: unit.unitId ?? "",
              quantity: unit.unitCount ?? 1,
              booking_id: selectedOrderItem?.booking?.id,
            })),
          })

          // Participants after quantity update
          const {
            data: { participants: participantsInfoAfterQuantityUpdate },
          } = await OrderServices.getParticipantsOfOrderItem(orderData.id, orderItemId)

          const newlyAddedParticipants: (GetParticipantsOfOrderItemResponseType["participants"][0] & {
            addonAndParticipantFieldAssociation?: {
              participantUnitId?: string
              participantGroupName?: string
            }
          })[] = participantsInfoAfterQuantityUpdate.filter(
            (newParticipant) =>
              !participantsInfoBeforeQuantityUpdate.find(
                (oldParticipant) => oldParticipant.id === newParticipant?.id
              )
          )

          // Below function is to get the booking_participant_id of the newly added add-on and update the association to the newly added participant
          const getBookingParticipantIdOfAddon = (addonUnit: addonDetailsUntType) => {
            const addonParticipantIndex = newlyAddedParticipants?.findIndex(
              (newParticipant) =>
                newParticipant?.unit?.id === addonUnit?.participantUnitId &&
                (typeof newParticipant?.addonAndParticipantFieldAssociation === "undefined" ||
                  (newParticipant?.addonAndParticipantFieldAssociation?.participantGroupName ===
                    addonUnit?.participantGroupName &&
                    newParticipant?.addonAndParticipantFieldAssociation?.participantUnitId ===
                      addonUnit?.participantUnitId))
            )
            if (addonParticipantIndex >= 0) {
              newlyAddedParticipants[addonParticipantIndex].addonAndParticipantFieldAssociation = {
                participantGroupName: addonUnit?.participantGroupName,
                participantUnitId: addonUnit?.participantUnitId,
              }
              return newlyAddedParticipants[addonParticipantIndex]?.id
            }
            return undefined
          }
          // Updating the add-ons
          if (participantsInfo?.addonDetails?.length && orderData?.id && orderItemId) {
            const deleteAddonsPayload: string[] = []
            const updateAddonsPayload: (AddAddonToOrderItemRequestType & {
              order_item_id?: string
            })[] = []
            participantsInfo.addonDetails.forEach((addon) => {
              addon.units?.forEach((addonUnit) => {
                if (addonUnit.unitCount === 0) {
                  if (addonUnit.id) deleteAddonsPayload.push(addonUnit.id)
                } else {
                  updateAddonsPayload.push({
                    product_id: addon.addonId,
                    booking_participant_id:
                      addon.availability === "participant"
                        ? addonUnit.participantId
                          ? addonUnit.participantId
                          : getBookingParticipantIdOfAddon(addonUnit)
                        : undefined,
                    order_item_id: addonUnit.id,
                    quantities: [
                      {
                        quantity: addonUnit.unitCount,
                        unit_id: addonUnit.unitId ?? "",
                      },
                    ],
                  })
                }
              })
            })

            // Deleting the existing add-on order-items
            if (deleteAddonsPayload.length) {
              // TODO: Need BE support to delete multiple item at once
              // eslint-disable-next-line no-restricted-syntax
              for (const deleteAddonPayload of deleteAddonsPayload) {
                // eslint-disable-next-line no-await-in-loop
                await OrderServices.deleteNonBookableOrderItem(orderData?.id, deleteAddonPayload)
              }
              // await Promise.all(
              //   deleteAddonsPayload.map(addonOrderItemId =>
              //     OrderServices.deleteNonBookableOrderItem(orderData?.id, addonOrderItemId)
              //   )
              // )
            }
            // Create/Update add-on
            if (updateAddonsPayload?.length) {
              const addMultipleAddonToOrderItemPayload = updateAddonsPayload.filter(
                (addon) => !addon?.order_item_id
              )
              const updateMultipleAddonToOrderItemPayload = updateAddonsPayload.filter((addon) =>
                Boolean(addon?.order_item_id)
              )
              // Creating an add-on
              if (addMultipleAddonToOrderItemPayload?.length) {
                await OrderServices.addMultipleAddonToOrderItem(orderData?.id, orderItemId, {
                  items: addMultipleAddonToOrderItemPayload,
                })
              }
              // Update the existing add-on order-item
              if (updateMultipleAddonToOrderItemPayload?.length) {
                // TODO: Need BE support to update multiple item at once
                // eslint-disable-next-line no-restricted-syntax
                for (const updateAddonPayload of updateMultipleAddonToOrderItemPayload) {
                  if (updateAddonPayload?.order_item_id)
                    // eslint-disable-next-line no-await-in-loop
                    await OrderServices.updateAddonOfOrderItem(
                      orderData?.id,
                      updateAddonPayload?.order_item_id,
                      updateAddonPayload
                    )
                }
                // await Promise.all(
                //   updateMultipleAddonToOrderItemPayload.map(updateAddonToOrderItemPayload => {
                //     if (updateAddonToOrderItemPayload?.order_item_id) {
                //       return OrderServices.updateAddonOfOrderItem(
                //         orderData?.id,
                //         updateAddonToOrderItemPayload?.order_item_id,
                //         {
                //           quantities: updateAddonToOrderItemPayload.quantities
                //         }
                //       )
                //     }
                //   })
                // )
              }
            }
          }

          // Below function is to get the booking_participant_id of the newly added participant
          const getBookingParticipantIdOfField = (
            participantFieldsDetails: Record<string, string | number | boolean | string[]>
          ) => {
            const participantIndex = newlyAddedParticipants?.findIndex(
              (newParticipant) =>
                newParticipant?.unit?.id === participantFieldsDetails?.participantUnitId &&
                (typeof newParticipant?.addonAndParticipantFieldAssociation === "undefined" ||
                  (newParticipant?.addonAndParticipantFieldAssociation?.participantGroupName ===
                    participantFieldsDetails?.participantGroupName &&
                    newParticipant?.addonAndParticipantFieldAssociation?.participantUnitId ===
                      participantFieldsDetails?.participantUnitId))
            )
            if (participantIndex >= 0) {
              newlyAddedParticipants[participantIndex].addonAndParticipantFieldAssociation = {
                participantGroupName: participantFieldsDetails?.participantGroupName as string,
                participantUnitId: participantFieldsDetails?.participantUnitId as string,
              }
              return newlyAddedParticipants[participantIndex]?.id
            }
            return undefined
          }
          // Updating the per participant fields
          if (participantsInfo?.participantsDetails?.length && orderData?.id && orderItemId) {
            const updatedParticipantsPayload: UpdatePerParticipantFieldsOfOrderRequestType[] = []
            participantsInfo?.participantsDetails?.forEach((participantDetails) => {
              Object.keys(participantDetails)
                .filter(
                  (fieldName) =>
                    !(
                      fieldName.includes("_id") ||
                      fieldName === "participantUnitId" ||
                      fieldName === "participantGroupName" ||
                      fieldName === "participantId" ||
                      fieldName === "participantIndex" ||
                      fieldName === "id"
                    )
                )
                .forEach((fieldName) =>
                  updatedParticipantsPayload.push({
                    id: participantDetails[`${fieldName}_order_item_field_id`]
                      ? (participantDetails[`${fieldName}_order_item_field_id`] as string)
                      : undefined,
                    field_id: participantDetails[`${fieldName}_id`] as string,
                    order_item_id: orderItemId,
                    booking_participant_id: participantDetails.participantId
                      ? (participantDetails.participantId as string)
                      : getBookingParticipantIdOfField(participantDetails),
                    value: participantDetails[fieldName],
                  })
                )
            })
            if (updatedParticipantsPayload.length) {
              await OrderServices.updatePerParticipantFieldsOfOrder(
                orderData?.id,
                updatedParticipantsPayload
              )
            }
          }

          // Updating the per booking fields
          if (participantsInfo?.perBookingFieldsDetails?.length)
            await handleUpdateBookingDetailsOfOrderItem({
              formValues: participantsInfo,
              orderData,
              orderItemId,
            })

          await onSuccess?.()
        } else {
          showToast({
            heading: "Unable to update the booking! please try again later.",
            type: "danger",
          })
        }
      } catch (error) {
        Sentry.captureException(error)
        await onError?.(error)
      }
    },
    [handleRescheduleActivityBooking, handleUpdateBookingDetailsOfOrderItem, showToast]
  )

  return { handleRescheduleActivityBookingWithFields }
}
